import {
  Button,
  ExpandableSection,
  Table,
  TableFiltering,
  TablePageSizeSelector,
  TablePagination,
  TablePreferences,
  TableSorting,
} from 'AWS-UI-Components-React';
import React from 'react';
import { AccuracyGranularity } from '../../constants/constants';
import { COMPONENT_TO_LINK } from '../../constants/urls';
import { downloadFile, toDownloadFormat, zip } from '../../utilities';
import logger from '../../utils/logging';
import metricsPublisher from '../../utils/metrics';
import { AccuracyButton } from '../buttons/accuracy_button';
import { getJSClient } from '../js_client';
import ComponentLink from './controls/component_link';
import { ErrorFlash } from './error_flash';

export class AccuracyDriversTable extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      items : [],
      downloadInProgress : false,
      loadInProgress: true,
      loadError: false,
      accuracyDriversExpanded: false,
      componentAccuracyDrivers: {},
      plAccuracyDrivers: {},
      loadStartTimestamp: 0,
      loadEndTimestamp: 0,
      granularity: AccuracyGranularity.COMPONENT,
      hiddenColumns : ['productLine', 'contributionToDriver'],
      columnDefinitions: [],
    };
    this.headings = {};
    this.keys = {};
  };

  processComponentData = (driversData) => {
    if(driversData && driversData.sortedHeadings && driversData.sortedKeys) {
      this.headings.component = driversData.sortedHeadings;
      this.keys.component = driversData.sortedKeys;
    }
    if(driversData && driversData.accuracyDriversList){
      const items = [];
      driversData.accuracyDriversList.forEach(e => items.push({
                                                                marketplaceId: this.props.marketplaceIdToLabel[e.marketplaceId],
                                                                channel: e.channel, component: e.component,
                                                                contributionToAccuracy: e.contributionToAccuracy,
                                                                perUnitNetVarianceLastCP: e.perUnitNetVarianceLastCP}));
      this.setState({loadInProgress: false, items, componentAccuracyDrivers: items}, () => this.updateColumnDefinitions());
    }
  };

  processPLData = (driversData) => {
    if(driversData && driversData.sortedHeadings && driversData.sortedKeys) {
      this.headings.pl = driversData.sortedHeadings;
      this.keys.pl = driversData.sortedKeys;
    }
    if(driversData && driversData.accuracyDriversList){
      const items = [];
      const combinedData = this.combineComponentAndPlData(this.state.componentAccuracyDrivers, driversData.accuracyDriversList);
      for (const e of combinedData) {
        if(e.productLine) {
          items.push({
                       marketplaceId: '',
                       channel: '', component: '',
                       productLine: e.productLine,
                       contributionToDriver: e.contributionToDriver,
                       contributionToAccuracy: '',
                       perUnitNetVarianceLastCP: e.perUnitNetVarianceLastCP});
        } else {
          items.push({
                       marketplaceId: e.marketplaceId,
                       channel: e.channel, component: e.component, productLine: e.productLine,
                       contributionToAccuracy: e.contributionToAccuracy,
                       contributionToDriver: '',
                       perUnitNetVarianceLastCP: e.perUnitNetVarianceLastCP});
        }
      }
      this.setState({loadInProgress: false, items, plAccuracyDrivers: items}, () => this.updateColumnDefinitions());
    }
  };

  combineComponentAndPlData = (componentData, plData) => {
    let driversMap = new Map();
    for(const componentDriver of componentData) {
      const key = this.getDriversKey(componentDriver);
      driversMap.set(key, [componentDriver]);
    }

    for(const plDriver of plData) {
      plDriver.marketplaceId = this.props.marketplaceIdToLabel[plDriver.marketplaceId];
      const key = this.getDriversKey(plDriver);
      if(driversMap.has(key)) {
        driversMap.get(key).push(plDriver);
      }
    }
    let data = [];
    for (const driver of componentData) {
      data.push.apply(data, driversMap.get(this.getDriversKey(driver)));
    }
    return data;
  }

  getDriversKey = (driver) => {
    const keys = [driver.marketplaceId, driver.component, driver.channel];
    return keys.join('-');
  }

  onAccuracyDriversExpandOrCollapse = () => {
    this.setState({accuracyDriversExpanded: !this.state.accuracyDriversExpanded});
    if(Object.keys(this.state.componentAccuracyDrivers).length === 0){
      this.setState({loadStartTimestamp: performance.now()});
      let payload = Object.assign({}, this.props.payload);
      payload.granularity = AccuracyGranularity.COMPONENT;
      logger.info('Submit accuracy drivers (component) request with payload ', payload);
      this.client.getAccuracyDrivers(
        (response) => {
          this.setState({loadEndTimestamp: performance.now()}, () => this.processComponentData(response.data));
          const responseTime = this.state.loadEndTimestamp - this.state.loadStartTimestamp;
          metricsPublisher.forMethod("accuracyDriversComponentLoadTime").publishTimer(responseTime);
          logger.info('Accuracy drivers (component) response time', {responseTime: responseTime, payload: payload});
        },
        (error) => {
          logger.error('Load accuracy drivers (component) failure', error, payload);
          this.setState({accuracyDriversExpanded: false, loadError: true});
        },
        payload);
    }
  };

  onDownloadData = () => {
    this.setState({ downloadInProgress: true });
    const data = toDownloadFormat(this.state.columnDefinitions, this.state.items);
    downloadFile(data, 'Accuracy_Drivers_'.concat(this.props.payload.snapshotId, '.xls'));
    this.setState({ downloadInProgress: false });
  };

  getComponentLink = (name) => {
    return (<ComponentLink componentName={name} mapping={COMPONENT_TO_LINK} level={0} />);
  };

  componentWillMount = () => {
    this.client = getJSClient();
  };

  updateColumnDefinitions = () => {
    let columnDefinitions = [];
    const columnKeys = this.state.granularity === AccuracyGranularity.COMPONENT.toLowerCase() ? this.keys.component : this.keys.pl;
    const columnHeadings = this.state.granularity === AccuracyGranularity.COMPONENT.toLowerCase() ? this.headings.component : this.headings.pl;
    for (let [key, heading] of zip(columnKeys, columnHeadings)) {
      if(key === 'component') {
        columnDefinitions.push({id: key, header: heading, cell: item => this.getComponentLink(item[key])});
      } else if (key === 'contributionToDriver' || key === 'contributionToAccuracy') {
        const placeholder = this.state.granularity === AccuracyGranularity.COMPONENT ? 'N/A' : '';
        columnDefinitions.push({id: key, header: heading, cell: item => item[key] ? item[key].toFixed(2) + '%' : placeholder});
      } else if (key === 'perUnitNetVarianceLastCP') {
        columnDefinitions.push({id: key, header: heading, cell: item => item[key] ? item[key].toFixed(2) : 'N/A' });
      } else {
        columnDefinitions.push({id: key, header: heading, cell: item => item[key]});
      }
    }
    const columnDef = columnDefinitions.filter((col) => !this.state.hiddenColumns.includes(col.id));
    this.setState({columnDefinitions:columnDef});
  }

  setGranularity(chosen) {
    const granularity = chosen.detail.selectedId;
    this.setState({granularity});
    if(granularity === AccuracyGranularity.COMPONENT.toLowerCase()){
      this.setState({items: this.state.componentAccuracyDrivers, hiddenColumns: ['productLine', 'contributionToDriver']},
                    () => this.updateColumnDefinitions());
    } else {
      this.setState({hiddenColumns: []});
      if(Object.keys(this.state.plAccuracyDrivers).length === 0) {
        let payload = Object.assign({}, this.props.payload);
        this.setState({loadInProgress: true, loadStartTimestamp: performance.now()});
        payload.granularity = AccuracyGranularity.PRODUCTLINE;
        logger.info('Submit accuracy drivers (PL) request with payload ', payload);
        this.client.getAccuracyDrivers(
          (response) => {
            this.setState({loadEndTimestamp: performance.now()}, () => this.processPLData(response.data));
            const responseTime = this.state.loadEndTimestamp - this.state.loadStartTimestamp;
            metricsPublisher.forMethod("accuracyDriversPLLoadTime").publishTimer(responseTime);
            logger.info('Accuracy drivers (PL) response time', {responseTime: responseTime, payload: payload});
          },
          (error) => {
            logger.error('Load accuracy drivers (PL) failure', error, payload);
          },
          payload);
      } else {
        this.setState({items: this.state.plAccuracyDrivers}, () => this.updateColumnDefinitions());
      }
    }
  }

  render() {

    const pagination = this.state.granularity === AccuracyGranularity.COMPONENT.toLowerCase() ? 20 : 18;
    const granularityItems = [
      {
        label: 'Show driver contribution to FA Inaccuracy (Abs)',
        id: 'component',
      },
      {
        label: 'Show top 5 PL Driver Inaccuracy Contribution',
        id: 'productline',
      }
    ];
    const sortableColumns = this.state.granularity === AccuracyGranularity.COMPONENT.toLowerCase() ? [
      { id: "contributionToAccuracy", field: "contributionToAccuracy" },
      { id: "perUnitNetVarianceLastCP", field: "perUnitNetVarianceLastCP" }] : [];

    const PAGE_SELECTOR_OPTIONS = [
      { value: 18, label: '18' },
      { value: 30, label: '30' },
      { value: 60, label: '60' },
      { value: 120, label: '120' }
      ];

    return (
      <div>
        <ExpandableSection header={"Accuracy Drivers"}
                           onChange={this.onAccuracyDriversExpandOrCollapse}
                           expanded={this.state.accuracyDriversExpanded}>
          <Table
            className="ZebraStripesTable accuracy-table-container"
            columnDefinitions={ this.state.columnDefinitions }
            items={this.state.items}
            stickyHeader
            loading={this.state.loadInProgress}
            loadingText="Loading table..."
            empty={
              <div className="awsui-util-t-c">
                <div className="awsui-util-pt-xs awsui-util-mb-xs">
                  <b>This table was empty or could not be found.</b>
                </div>
              </div>
            }>
            <Button
              text="Download"
              variant="normal"
              disabled={this.state.loadInProgress || this.state.loadError}
              loading={this.state.downloadInProgress}
              onClick={this.onDownloadData}
            />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <TableFiltering filteringPlaceholder="Search Marketplace/Channel/Component" />
            <TablePagination pageSize={pagination} />
            {this.state.granularity === AccuracyGranularity.PRODUCTLINE.toLowerCase() &&
              <TablePreferences id="table-preferences" title="Preferences" confirmLabel="Confirm" cancelLabel="Cancel">
                <TablePageSizeSelector title="Page size" options={PAGE_SELECTOR_OPTIONS} value={pagination} />
              </TablePreferences>
            }
            {this.props.featureList && this.props.featureList.includes('accuracy-drivers-pl') &&
              <div style={{paddingTop: '17px', paddingRight: '25px'}}>
                <AccuracyButton
                  items={granularityItems}
                  selected={this.state.granularity}
                  disabled={this.state.loadInProgress || this.state.loadError}
                  onChange={this.setGranularity.bind(this)}
                />
              </div>
            }
            <TableSorting sortingColumn="id"
                          sortingDescending={true}
                          sortableColumns={sortableColumns} />
          </Table>
        </ExpandableSection>
        {this.state.loadError && (
          <ErrorFlash type="Accuracy Drivers"/>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/content/accuracy_drivers_table.jsx